"use client";

import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { useEffect, useRef, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import type { Database } from "@/lib/types/supabase";

/**
 * Real-time hook for all user's set reviews in a festival
 * - Subscribes to all set_reviews changes for a user in a festival
 * - Provides instant updates across tabs/devices
 * - Single source of truth for festival's set reviews
 * - Listens for optimistic deletion events from individual review hooks
 */

// Event constants for cross-component communication
const REVIEW_DELETED_EVENT = "review-deleted";

// Type-safe event detail interface
interface ReviewDeletedDetail {
	setId: string;
	userId: string;
}

export interface SetReview {
	id: string;
	setId: string;
	artistId: string | null;
	userId: string;
	rating: number | null;
	content: string | null;
	loved: boolean;
	sawLive: boolean;
	sawStream: boolean;
	wantToSee: boolean;
	mustSee: boolean;
	createdAt: string;
	updatedAt: string;
}

interface UseRealtimeUserSetReviewsOptions {
	festivalId: string;
	userId: string | null;
	enabled?: boolean;
}

export function useRealtimeUserSetReviews({
	festivalId,
	userId,
	enabled = true,
}: UseRealtimeUserSetReviewsOptions) {
	const [reviews, setReviews] = useState<SetReview[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const channelRef = useRef<any>(null);
	const instanceId = useRef(Math.random().toString(36).substring(7));
	const supabase = createClient();

	// Initial data fetch
	useEffect(() => {
		if (!userId || !festivalId || !enabled) {
			setIsLoading(false);
			setReviews([]);
			return;
		}

		async function fetchInitialReviews() {
			if (!userId) return;

			try {
				type SetReviewWithFestival = Database["public"]["Tables"]["set_reviews"]["Row"] & {
					sets: { festival_id: string };
				};

				const { data, error } = await supabase
					.from("set_reviews")
					.select(`
						*,
						sets!inner(
							festival_id
						)
					`)
					.eq("user_id", userId)
					.eq("sets.festival_id", festivalId)
					.returns<SetReviewWithFestival[]>() as { data: SetReviewWithFestival[] | null; error: any };

				if (!error && data) {
					const transformedReviews = data
						.filter((review) => review.set_id !== null)
						.map((review) => ({
							id: review.id,
							setId: review.set_id as string,
							artistId: review.artist_id,
							userId: review.user_id,
							rating: review.rating,
							content: review.content,
							loved: review.loved,
							sawLive: review.saw_live,
							sawStream: review.saw_stream,
							wantToSee: review.want_to_see,
							mustSee: review.must_see,
							createdAt: review.created_at,
							updatedAt: review.updated_at,
						}));
					setReviews(transformedReviews);
				} else {
					setReviews([]);
				}
			} catch (error) {
				console.error("Error fetching initial set reviews:", error);
				setReviews([]);
			} finally {
				setIsLoading(false);
			}
		}

		fetchInitialReviews();
	}, [userId, festivalId, enabled, supabase]);

	// Listen for optimistic updates from toggle/modal — keeps serverReviews in sync
	// without waiting for Supabase Realtime, preventing cleanup from wiping optimistic state
	useEffect(() => {
		if (!userId || !enabled) return;

		const handleReviewUpdate = (event: Event) => {
			const { userId: eventUserId, setId, artistId, review: eventReview } =
				(event as CustomEvent).detail;
			if (eventUserId !== userId) return;

			setReviews((prev) => {
				if (eventReview === null) {
					// Deletion
					return prev.filter((r) => r.setId !== setId);
				}
				const existingIndex = prev.findIndex((r) => r.setId === setId);
				const updatedReview: SetReview = {
					id: eventReview.id || `optimistic-${setId}`,
					setId,
					artistId: artistId || eventReview.artistId,
					userId,
					rating: eventReview.rating ?? 0,
					content: eventReview.content ?? null,
					loved: eventReview.loved ?? false,
					sawLive: eventReview.sawLive ?? false,
					sawStream: eventReview.sawStream ?? false,
					wantToSee: eventReview.wantToSee ?? false,
					mustSee: eventReview.mustSee ?? false,
					createdAt: eventReview.createdAt || new Date().toISOString(),
					updatedAt: new Date().toISOString(),
				};
				if (existingIndex >= 0) {
					const next = [...prev];
					next[existingIndex] = updatedReview;
					return next;
				}
				return [...prev, updatedReview];
			});
		};

		const handleReviewDeleted = (event: Event) => {
			const customEvent = event as CustomEvent<ReviewDeletedDetail>;
			if (customEvent.detail.userId === userId) {
				setReviews((prev) =>
					prev.filter((r) => r.setId !== customEvent.detail.setId),
				);
			}
		};

		window.addEventListener("set-review-update", handleReviewUpdate);
		window.addEventListener(REVIEW_DELETED_EVENT, handleReviewDeleted);
		return () => {
			window.removeEventListener("set-review-update", handleReviewUpdate);
			window.removeEventListener(REVIEW_DELETED_EVENT, handleReviewDeleted);
		};
	}, [userId, enabled]);

	// Real-time subscription for all user's reviews
	useEffect(() => {
		if (!userId || !festivalId || !enabled) return;

		// Subscribe to all set_reviews changes for this user
		// Note: We can't filter by festival_id directly, so we filter client-side
		const channelName = `user-set-reviews:${userId}:${instanceId.current}`;

		const channel = supabase
			.channel(channelName)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "set_reviews",
					filter: `user_id=eq.${userId}`,
				},
				async (payload: RealtimePostgresChangesPayload<any>) => {
					// For INSERT/UPDATE, check if this set belongs to our festival
					if (
						payload.eventType === "INSERT" ||
						payload.eventType === "UPDATE"
					) {
						const setId = payload.new.set_id;

						// Check if this set belongs to our festival
						type SetSelect = Pick<Database["public"]["Tables"]["sets"]["Row"], "festival_id">;
						const { data: setData } = await supabase
							.from("sets")
							.select("festival_id")
							.eq("id", setId)
							.single() as { data: SetSelect | null; error: any };

						if (setData?.festival_id !== festivalId) {
							return; // Ignore reviews for other festivals
						}

						const newReview: SetReview = {
							id: payload.new.id,
							setId: payload.new.set_id,
							artistId: payload.new.artist_id,
							userId: payload.new.user_id,
							rating: payload.new.rating,
							content: payload.new.content,
							loved: payload.new.loved,
							sawLive: payload.new.saw_live,
							sawStream: payload.new.saw_stream,
							wantToSee: payload.new.want_to_see,
							mustSee: payload.new.must_see,
							createdAt: payload.new.created_at,
							updatedAt: payload.new.updated_at,
						};

						setReviews((prev) => {
							const existingIndex = prev.findIndex(
								(r) => r.setId === newReview.setId,
							);
							if (existingIndex >= 0) {
								// Update existing review
								const updated = [...prev];
								updated[existingIndex] = newReview;
								return updated;
							} else {
								// Add new review
								return [...prev, newReview];
							}
						});
					} else if (payload.eventType === "DELETE") {
						// Remove deleted review
						setReviews((prev) =>
							prev.filter((r) => r.setId !== payload.old.set_id),
						);
					}
				},
			)
			.subscribe((status) => {
				// Handle subscription errors gracefully
				if (status === "CHANNEL_ERROR") {
					console.warn(
						`[Realtime] Channel error for user set reviews. Will retry on reconnect.`
					);
				} else if (status === "TIMED_OUT") {
					console.warn(
						`[Realtime] Connection timeout for user set reviews. Will retry.`
					);
				}
			});

		channelRef.current = channel;

		return () => {
			if (channelRef.current) {
				supabase.removeChannel(channelRef.current);
			}
		};
	}, [userId, festivalId, enabled, supabase]);

	return {
		reviews,
		isLoading,
	};
}
